import React, { useState } from "react";
import { Link } from "react-router-dom";
import useShortenedUrls from "../../hooks/useShortenedUrls";
import useURLActions from "../../hooks/useURLActions";
import URLEntry from "../url/URLEntry";
import FormField from "../shared/FormField";
import Button from "../shared/Button";
import LogoSelector from "../shared/LogoSelector";
import { PREFIX_OPTIONS } from "../../constants/prefixes";
import "../../styles/Shortener.css";
import { useLogos } from "../../hooks/useLogos";
import { useUserId } from "../../hooks/useUserId";
import { usePopup } from "../../hooks/usePopup";
import useQRCodeDownloader from "../../hooks/useQRCodeDownloader";
import { useClientPrincipal } from "../../context/ClientPrincipalContext";
import Logo from "../../assets/images/Shorten-Logo.svg";

const Shortener: React.FC = () => {
  const { clientPrincipal } = useClientPrincipal();
  const userId = useUserId();
  const { showPopup, shortenedCode, openPopup, closePopup } = usePopup();
  const [selectedLogoId, setSelectedLogoId] = useState<string>("cgi");

  const {
    allLogos,
    deleteLogo,
    uploadLogo,
    validateLogoFile,
    validateLogoName,
  } = useLogos(userId);

  const { setShortenedUrls, shortenedUrls } = useShortenedUrls();
  const { handleDownloadQRCode } = useQRCodeDownloader(shortenedUrls);
  const {
    originalUrl,
    setOriginalUrl,
    name,
    setName,
    expirationDate,
    setExpirationDate,
    prefix,
    setPrefix,
    handleShorten,
  } = useURLActions(setShortenedUrls, clientPrincipal);

  const handleShortenClick = async () => {
    const shortCode = await handleShorten(selectedLogoId);
    if (shortCode) {
      openPopup(shortCode);
    }
  };

  const shortenedData = shortenedCode ? shortenedUrls[shortenedCode] : null;

  return (
    <div className="shortify-container">
      <div className="shortify-form-wrapper">
        <div className="shortify-form-card">
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              marginBottom: "18px",
            }}
          >
            <div className="linktree-empty" style={{ margin: 0, flex: 1 }}>
              <h3>Shorten your URL</h3>
              <p>Create a short link and QR code for easy sharing.</p>
            </div>

            <img
              src={Logo}
              alt="Shortify Logo"
              style={{
                width: "56px",
                height: "auto",
                objectFit: "contain",
                flexShrink: 0,
              }}
            />
          </div>
          <FormField
            label="URL"
            value={originalUrl}
            onChange={(e) => setOriginalUrl(e.target.value)}
            placeholder="Please enter a URL"
            required
          />
          <FormField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Please enter a name"
          />
          <div className="logo-form-field">
            <LogoSelector
              selectedLogoId={selectedLogoId}
              onLogoSelect={setSelectedLogoId}
              allLogos={allLogos}
              onLogoDelete={deleteLogo}
              onLogoUpload={uploadLogo}
              validateLogoFile={validateLogoFile}
              validateLogoName={validateLogoName}
            />
            <label className="logo-field-label">Logo</label>
          </div>
          <div className="form-row">
            <FormField
              label="Prefix"
              type="select"
              value={prefix}
              onChange={(e) => setPrefix(e.target.value)}
              options={PREFIX_OPTIONS}
            />
            <FormField
              label="Expiration Date"
              type="date"
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.target.value)}
            />
          </div>
          <Button
            variant="primary"
            onClick={handleShortenClick}
            className="shortener-submit-button"
          >
            Shorten
          </Button>
        </div>

      </div>

      {showPopup && shortenedData && (
        <>
          <div className="popup-backdrop" onClick={closePopup} />
          <div className="floating-popup">
            <URLEntry
              shortCode={shortenedCode!}
              url={shortenedData.url}
              name={shortenedData.name}
              expirationDate={shortenedData.expirationDate}
              isFavourite={shortenedData.isFavourite}
              fileFormat={shortenedData.fileFormat}
              prefix={shortenedData.prefix}
              showHeader={false}
              useAnimatedQRCode={true}
              onDelete={() => {}}
              onCopy={() =>
                navigator.clipboard.writeText(
                  `https://www.shortfy.it/api/go/${shortenedCode}`,
                )
              }
              onFileFormatChange={() => {}}
              onDownloadQRCode={handleDownloadQRCode}
              onRedirect={() => {}}
              onToggleFavourite={() => {}}
              onUpdate={() => {}}
              defaultExpanded={true}
              withLogo={true}
              logoId={selectedLogoId}
            />
            <p className="popup-info-message">
              The QR Code and URL are now saved in{" "}
              <Link to="/my-urls" className="popup-info-link">
                My URL Page
              </Link>
              .
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default Shortener;
